import { dateFormatter, DateFormatTypes, getHHMMSS } from '@jfs/utils'
import { flow, Instance, types } from 'mobx-state-tree'
import ic_copy from '../../assets/images/ic_copy.png'
import ic_lock from '../../assets/images/ic_lock.png'
import { fetchAllBanks } from '../../network/fetchAllBanks'
import { generateOTP } from '../../network/generateOTP'
import { getBanks } from '../../network/getBanks'
import { getOTPAuthCodeApi } from '../../network/getOTPAuthCodeApi'
import { getSummaryV3 } from '../../network/getSummaryV3'
import { getUserSettings } from '../../network/getUserSettings'
import { getVideoUrls } from '../../network/getVideoUrls'
import { getCommaFormatedVal, getSelectedDate, isMobile } from '../../util/util'
import { getGenerateOtpReqForApiCred } from '../request/requestbody'
import { initRefundSearchFiltersData } from '../static/data'
import { customError } from '../static/strings'
import { RootStore } from '../store'
import { Config, configData } from './Config'
import { NotificationSetting, notificationSettingData, NotificationType } from './NotificationSetting'

const BankList = types.model('BankList', {
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
  rank: types.optional(types.number, 0),
  isPopular: types.optional(types.boolean, false),
  logo: types.optional(types.string, ''),
  ifsc: types.optional(types.string, ''),
  addressLine: types.optional(types.string, ''),
  accountNumber: types.optional(types.string, ''),
  iin: types.optional(types.string, ''),
  bankCode: types.optional(types.string, ''),
  ifscCode: types.optional(types.string, ''),
  accountName: types.optional(types.string, ''),
})

const Videos = types.model('Videos', {
  key: types.optional(types.string, ''),
  image: types.optional(types.string, ''),
  videoUrl: types.optional(types.string, ''),
  heading: types.optional(types.string, ''),
})

const Position = types.model('position', {
  left: types.number,
  top: types.number,
  visible: types.boolean,
})

const DateRange = types.model('date', {
  fromDate: types.Date,
  toDate: types.Date,
  position: Position,
  dateLabel: types.optional(types.string, ''),
})

const ApiCredentials = types.model('date', {
  mid: types.string,
  keyValue: types.string,
  clientId: types.string,
  creationDate: types.string,
})

const RecentSearch = types.model('recents', {
  txnId: types.string,
  filterKey: types.string,
})

const MetaData = types
  .model('MetaData', {
    trackingId: types.string,
    signupLineOfBusiness: types.string,
    signupBusinessType: types.string,
    signupAcceptedPayments: types.optional(types.array(types.string), ['']),
    signupAnnualRevenue: types.string,
    signupCompanyName: types.string,
    signupCompanyAddress: types.string,
    signupRoleInCompany: types.string,
    signupEmail: types.string,
    signupMobile: types.string,
    signupPincode: types.string,
    selectedMercName: types.optional(types.string, ''),
    selectedMobileNumber: types.optional(types.string, ''),
    selectedMercLegalName: types.optional(types.string, ''),
    selectedInstitutionId: types.string,
    selectedMerchantStatus: types.string,
    recentSearches: types.array(RecentSearch),
    stepKey: types.optional(types.string, 'contactInfo'),
    bankList: types.array(BankList),
    mobileNumber: types.string,
    selectedBankId: types.optional(types.string, ''),
    progress: types.optional(types.number, 0),
    videos: types.array(Videos),
    isAppLockEnabled: types.optional(types.boolean, false),
    isSetAppLockLater: types.optional(types.boolean, false),
    selectedDate: DateRange,
    dateValue: types.string,
    bankName: types.optional(types.string, ''),
    associatedUserBank: types.array(BankList),
    isIFSCValid: types.optional(types.boolean, false),
    totalTransactionAmount: types.optional(types.string, ''),
    totalTransactionCount: types.optional(types.string, ''),
    todaysSuccessfulTxnsCount: types.optional(types.string, ''),
    todaysSuccessfulTxnsAmt: types.optional(types.string, ''),
    todaysSuccessfulRefundsAmt: types.optional(types.string, ''),
    successfulTxnsAmt: types.optional(types.string, '0'),
    successfulTxnsCount: types.optional(types.string, ''),
    successfulRefundsAmt: types.optional(types.string, ''),
    totalRefundAmount: types.optional(types.string, ''),
    totalRefundCount: types.optional(types.string, ''),
    successfulRefundsCount: types.optional(types.string, ''),
    totalSettlementAmount: types.optional(types.string, '0'),
    toBeSettled: types.optional(types.string, ''),
    vpa: types.optional(types.string, ''),
    staticQr: types.optional(types.string, ''),
    fileUploadProgress: types.optional(types.number, 0),
    refundedId: types.optional(types.string, ''),
    showSearchResult: types.optional(types.boolean, false),
    searchString: types.optional(types.string, ''),
    searchFilterItem: types.optional(types.string, 'Merchant Ref No.'),
    disableBulkRefund: types.optional(types.boolean, false),
    downloadReportType: types.optional(types.string, 'Transaction'),
    isUserNotFoundInIdam: types.optional(types.boolean, false),
    dateTabIndex: types.optional(types.number, 0),
    isSplitSettlementsEnabled: types.optional(types.boolean, false),
    showChatbotAwareness: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    setDateTabIndex(index: number) {
      self.dateTabIndex = index
    },
    setShowChatbotAwareness(show: boolean) {
      self.showChatbotAwareness = show
    },
    setProgress() {
      const progress = self.progress
      if (progress < 100) {
        self.progress += 50
      } else {
        self.progress = 50
      }
    },
    updateIFSCValidity(isValid: boolean) {
      self.isIFSCValid = isValid
    },
    setSearchResultVisibility(isVisible: boolean) {
      self.showSearchResult = isVisible
    },
    setSearchString(text: string) {
      self.searchString = text
    },
    setSearchFilterItem(key: string) {
      self.searchFilterItem = key
    },
    updateIsSplitSettlementsEnabled(val: boolean) {
      self.isSplitSettlementsEnabled = val
    },
  }))
  .actions((self) => ({
    updateMobileNumber(mobile: string): void {
      const own = self
      own.mobileNumber = String(mobile ?? '').replace('+91', '') ?? ''
    },
    setBankName(name: string): void {
      const own = self
      own.bankName = name
    },
    updateSelectedMercName(name: string): void {
      const own = self
      own.selectedMercName = name
    },
    updateSelectedMercMobileNumber(mobile: string): void {
      const own = self
      own.selectedMobileNumber = mobile
    },
    updateSelectedMercStatus(status: string): void {
      const own = self
      own.selectedMerchantStatus = status
    },

    updateSelectedMercInstitutionId(id: string): void {
      const own = self
      own.selectedInstitutionId = id
    },
    updateLineOfBusiness(value: string): void {
      const own = self
      own.signupLineOfBusiness = value
    },
    updateBusinessType(value: string): void {
      const own = self
      own.signupBusinessType = value
    },
    updateAnnualRevenue(value: string): void {
      const own = self
      own.signupAnnualRevenue = value
    },
    updateAcceptedPayments(acceptenceArray: string[]): void {
      const own = self
      acceptenceArray.map((item) => {
        own.signupAcceptedPayments.push(item)
        return item
      })
    },
    updateRole(value: string): void {
      const own = self
      own.signupRoleInCompany = value
    },
    updateRegEmail(value: string): void {
      const own = self
      own.signupEmail = value
    },
    updateRegMobileNumber(value: string): void {
      const own = self
      own.signupMobile = value
    },
    updateRegPincode(value: string): void {
      const own = self
      own.signupPincode = value
    },
    setRefundedId(value: string): void {
      const own = self
      own.refundedId = value
    },
    setSignInData(data: any): void {
      const own = self
      own.signupCompanyName = data.companyName
      own.signupAnnualRevenue = data.annualRevenue
      own.signupCompanyAddress = data.companyAddress
      own.signupPincode = data.pincode
    },
    updateSearhRecents(searchTrxnId: string, filterKey: string): void {
      const own = self
      const foundRecent = own.recentSearches.find(
        (recent) => recent?.txnId === searchTrxnId && recent?.filterKey === filterKey,
      )
      !foundRecent && own.recentSearches.unshift({ txnId: searchTrxnId, filterKey: filterKey })
    },
    clearRecentSearches(): void {
      const own = self
      own.recentSearches = [] as any
    },
    fetchBanks: flow(function* fetchBanks() {
      self.setProgress()
      let response
      try {
        response = yield getBanks(
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
          self.mobileNumber,
        )
        self.bankList = response
        self.setProgress()
      } catch (e) {
        self.setProgress()
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    getAllBanks: flow(function* getAllBanks() {
      let response
      try {
        response = yield fetchAllBanks(
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
          self.mobileNumber,
        )
        self.bankList = response
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? customError.errorOccured)
      }
      return response
    }),
    updateBankList(list: any): void {
      const own = self
      // own.bankList = JSON.parse(JSON.stringify([...own.bankList, ...list]))
      own.associatedUserBank = JSON.parse(JSON.stringify(list))
    },
    updateSelectedBankId(id: string): void {
      const own = self
      own.selectedBankId = id
    },
    getVideos: flow(function* getVideos() {
      let data: Array<VideosType> = []
      try {
        const req = {
          inputFilter: ['learn_series', 'tnc'],
          configParams: {
            mediaType: 'JSON',
            sortColumn: null,
            page: 1,
            recordCount: 8,
          },
        }
        const res = yield getVideoUrls(req, RootStore.user.session.getTokens)
        if (res?.status === 'SUCCESS') {
          const list = res.data?.learn_series?.content
          list?.forEach((item: any, index: number) => {
            data.push({
              key: String(index),
              image: item?.image_thumbnail,
              videoUrl: item?.url,
              heading: item?.heading,
            })
          })
          self.videos = JSON.parse(JSON.stringify(data))
        }
      } catch (e) {
        // RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }

      return data
    }),
    setAppLockEnabled(isLockEnabled: boolean): void {
      const own = self
      own.isAppLockEnabled = isLockEnabled
    },
    setAppLockLater(isLockEnabled: boolean): void {
      const own = self
      own.isSetAppLockLater = isLockEnabled
    },
    updateDates(dateObj: any): void {
      const own = self
      own.selectedDate.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      own.selectedDate.toDate = dateObj?.toDate ?? new Date(Date.now())
      own.selectedDate.position.top = dateObj?.position?.top ?? 0
      own.selectedDate.position.left = dateObj?.position?.left ?? 0
      own.selectedDate.position.visible = dateObj?.position?.visible ?? false
      own.selectedDate.dateLabel = dateObj?.dateLabel
    },
    setDateValue(data: string) {
      const own = self
      own.dateValue = data
    },
    fetchStatsV3: flow(function* fetchStatsV3(
      mid: string,
      type: string,
      selectedDateInfo: any,
      isTodaysTransactionsSummary?: boolean,
      isCustomFromRecentsParam = false,
      isLoadMore = false,
      callRefundsApi = true,
      abortController: any = null,
    ) {
      let response
      const filteredFromDate = selectedDateInfo?.fromDate ?? self?.selectedDate?.fromDate ?? new Date(Date.now())
      const isCustomFromRecents =
        isCustomFromRecentsParam ||
        selectedDateInfo?.dateLabel === 'Custom Range' ||
        selectedDateInfo?.dateLabel === 'custom'
      const formattedFilteredFromData = `${dateFormatter(filteredFromDate, DateFormatTypes.YYYYMMDD)} ${getHHMMSS(
        filteredFromDate,
      )}`
      const filteredEndDate = selectedDateInfo?.toDate ?? self?.selectedDate?.toDate ?? new Date(Date.now())
      const formattedFilteredToData = `${dateFormatter(filteredEndDate, DateFormatTypes.YYYYMMDD)} ${getHHMMSS(
        filteredEndDate,
      )}`
      const txnReq = {
        inputFilter: {
          merchantId: mid,
          transactionType: 'PURCHASE',
          startDateTime: formattedFilteredFromData,
          endDateTime: formattedFilteredToData,
          isSMID: RootStore.user.isSmid ? 'Y' : 'N',
        },
      }

      const refundReq = {
        inputFilter: {
          merchantId: mid,
          transactionType: 'REFUND',
          startDateTime: formattedFilteredFromData,
          endDateTime: formattedFilteredToData,
          isSMID: RootStore.user.isSmid ? 'Y' : 'N',
        },
      }
      const settlementReq = {
        inputFilter: {
          merchantId: mid,
          groupBy: ['REF_NUMBER'],
          startDateTime: formattedFilteredFromData,
          endDateTime: formattedFilteredToData,
          isSMID: RootStore.user.isSmid ? 'Y' : 'N',
        },
      }
      const getReq: any = {
        transactions: txnReq,
        settlements: settlementReq,
        refunds: refundReq,
      }
      try {
        response = yield getSummaryV3(
          RootStore.user.session.getTokens,
          self.mobileNumber,
          getReq[type],
          type,
          isCustomFromRecents,
          selectedDateInfo ?? self?.selectedDate,
          abortController,
        )
        if (type === 'transactions') {
          let refundresponse
          if ((isTodaysTransactionsSummary || !isMobile(true)) && callRefundsApi) {
            refundresponse = yield getSummaryV3(
              RootStore.user.session.getTokens,
              self.mobileNumber,
              refundReq,
              'refunds',
              isCustomFromRecents,
              selectedDateInfo ?? self?.selectedDate,
              abortController,
            )
          }
          if (isTodaysTransactionsSummary) {
            self.todaysSuccessfulTxnsCount = response?.successfulTxnsCount ?? ''
            self.todaysSuccessfulTxnsAmt = response?.successfulTxnsAmt ?? ''
            self.todaysSuccessfulRefundsAmt = refundresponse?.successfulRefundsAmt ?? ''
            self.successfulTxnsAmt = response?.successfulTxnsAmt ? (+response?.successfulTxnsAmt).toFixed(2) : '0'
          } else {
            self.totalTransactionCount = response?.totalTransactionCount ?? ''
            self.successfulTxnsCount = response?.successfulTxnsCount ?? ''
            self.totalTransactionAmount = response?.totalTransactionAmount ?? ''
            self.successfulTxnsAmt = response?.successfulTxnsAmt ? (+response?.successfulTxnsAmt).toFixed(2) : '0'
            self.totalRefundAmount = refundresponse?.totalRefundAmount ?? '0'
            self.successfulRefundsAmt = refundresponse?.successfulRefundsAmt ?? '0'
            self.successfulRefundsCount = refundresponse?.successfulRefundsCount ?? '0'
            self.totalRefundCount = refundresponse?.totalRefundCount ?? '0'
          }
        } else if (type === 'settlements') {
          self.totalSettlementAmount = response?.totalSettlementAmount
            ? (+response?.totalSettlementAmount).toFixed(2)
            : '0'
          self.toBeSettled = response?.tobeSettled ? (+response?.tobeSettled).toFixed(2) : '0'
          RootStore.settlements.setSettlementDataFromStats(response, isLoadMore)
        } else if (type === 'refunds') {
          self.totalRefundAmount = response?.totalRefundAmount ?? '0'
          self.successfulRefundsAmt = response?.successfulRefundsAmt ?? '0'
          self.successfulRefundsCount = response?.successfulRefundsCount ?? '0'
          self.totalRefundCount = response?.totalRefundCount ?? '0'
        }
      } catch (e) {
        // if (isRequestPoll) {
        //   RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        // }
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    updateVpa(vpa: string): void {
      const own = self
      own.vpa = vpa
    },
    setFileUploadProgress(progress: number): void {
      const own = self
      own.fileUploadProgress = progress
    },
    updateBulkRefundDisabled(value: boolean): void {
      const own = self
      own.disableBulkRefund = value
    },
    updateMerchantQrAndVpa(data: any): void {
      console.log('merchantQrAndVpa', data)
      const own = self
      own.vpa = data?.vpa
      own.staticQr = data?.qr
      self.selectedMercLegalName = data?.businessName
    },
    setDownloadReportType(data: string) {
      if (self.downloadReportType !== data) {
        RootStore.reports.resetPagination()
        self.downloadReportType = data
      }
    },
    setIsUserNotFoundInIdam(status: boolean) {
      console.log('===> setIsUserNotFoundInIdam', status)
      const own = self
      own.isUserNotFoundInIdam = status
    },
  }))
  .views((self) => ({
    getRecentSearches(type?: any): any {
      let retData: any = []

      if (type === 'initrefund') {
        const initRefundSearchData = initRefundSearchFiltersData.map((item: any) => item.value)
        self.recentSearches.map((item: any) => {
          if (initRefundSearchData.includes(item.filterKey)) {
            retData.push(item)
          }
        })
      } else {
        retData = self.recentSearches
      }
      return retData
    },

    getVideoList() {
      return JSON.parse(JSON.stringify(self.videos))
    },

    getStats() {
      return [
        `${getCommaFormatedVal(Number(self.successfulTxnsAmt ?? '0'), false)}`,
        Number(self.successfulTxnsCount),
        `${getCommaFormatedVal(Number(self.successfulRefundsAmt ?? '0'), false)}`,
        Number(self.successfulRefundsCount),
      ]
    },
    getRefundStats() {
      const successRate =
        Number(self.totalRefundCount) > 0
          ? `${Number((Number(self.successfulRefundsCount) / Number(self.totalRefundCount)) * 100).toFixed(2)} %`
          : 'NA'
      return [
        Number(self.totalRefundCount),
        `${getCommaFormatedVal(Number(self.totalRefundAmount ?? '0'), false)}`,
        `${getCommaFormatedVal(Number(self.successfulRefundsAmt ?? '0'), false)}`,
        successRate,
      ]
    },
    getSummary() {
      const retData = [
        {
          title: "Today's Sale",
          value: Number(self.todaysSuccessfulTxnsAmt ?? ''),
          id: 0,
        },
        {
          title: "Today's Transactions",
          value: Number(self.todaysSuccessfulTxnsCount ?? ''),
          id: 1,
        },
        {
          title: "Today's Refunds",
          value: Number(self.todaysSuccessfulRefundsAmt ?? ''),
          id: 2,
        },
      ]
      return retData
    },
    getBankList() {
      const retData: any = []
      const bankList = self.bankList
      if (bankList.length === 0) {
        return []
      }
      const popular = bankList
        .toJSON()
        .map((item: BankListType) => {
          if (item.isPopular && item.ifsc === '') {
            return { id: item.id, text: item.name, imageUrl: item.logo }
          }
          return ''
        })
        .filter((item: any) => item !== '')
      if (popular.length !== 0) {
        retData.push({
          title: 'Popular banks',
          data: popular.length > 6 ? popular.slice(0, 6) : popular,
        })
      }
      // TODO: remove external image
      const others = bankList
        .toJSON()
        ?.map((item: BankListType) => {
          if (!item.isPopular && item.ifsc === '') {
            return {
              id: item.id,
              text: item.name,
              bankCode: item?.bankCode,
              iin: item?.iin,
              ifscCode: item?.ifscCode,
              imageUrl: item.logo || 'https://pngimg.com/uploads/bank/bank_PNG3.png',
              accountName: item?.accountName ?? '',
            }
          }
          return ''
        })
        .filter((item: any) => item !== '')

      if (others.length !== 0) {
        retData.push({
          title: 'Other banks',
          data: others,
        })
      }
      return retData
    },
    getLinkedBanks() {
      const own = self
      const banks: any = []
      own?.associatedUserBank.map((item, index) => {
        if (item.ifsc) {
          banks.push({
            id: index,
            logo: item.logo,
            accountNumber: item.accountNumber,
            ifsc: item.ifsc,
            addressLine: item.addressLine,
            name: item.name,
            accHolderName: item?.accountName ?? '',
          })
        }
      })
      return banks
    },
    getMobileNumber(): string {
      return self.mobileNumber
    },
    getAllBankNames() {
      const retData: any = []
      const bankList = self.bankList
      if (bankList.length === 0) {
        return []
      }
      bankList.map((item: any) => {
        const bankItem = {
          id: item.id,
          name: item.name,
        }
        retData.push(bankItem)
      })
      return retData
    },
    getSearchString() {
      return self.searchString
    },
    getSearchFilterItem() {
      return self.searchFilterItem
    },
    merchantQrAndVpa() {
      const merchantQrData = {
        staticQr: self.staticQr,
        vpa: self.vpa,
      }
      return merchantQrData
    },
  }))

const Settings = types
  .model('Settings', {
    notificationSettings: types.array(NotificationSetting),
    notificationMobileNumber: types.string,
    notificationEmailId: types.string,
    config: types.maybe(Config),
    metaData: MetaData,
    apiCredentials: ApiCredentials,
  })
  .actions((self) => ({
    updateUserCommunicationSettings(
      successTranSms: boolean,
      successTranEmail: boolean,
      successTranPush: boolean,
      settlementTranSms: boolean,
      settlementTranEmail: boolean,
    ): void {
      const own = self
      if (successTranSms) {
        const sTxnSms = NotificationSetting.create({
          channel: ['y', 'n', 'n'], // [portal = no, email= no, sms=yes]
          type: NotificationType.successTransaction,
        })
        own.notificationSettings.push(sTxnSms)
      }
      if (successTranEmail) {
        const sTxnEmail = NotificationSetting.create({
          channel: ['n', 'y', 'n'],
          type: NotificationType.successTransaction,
        })
        own.notificationSettings.push(sTxnEmail)
      }
      if (successTranPush) {
        const sTxnPush = NotificationSetting.create({
          channel: ['y', 'n', 'n'],
          type: NotificationType.successTransaction,
        })
        own.notificationSettings.push(sTxnPush)
      }
      if (settlementTranSms) {
        const setlTxnSms = NotificationSetting.create({
          channel: ['n', 'y', 'n'],
          type: NotificationType.settlementReportGen,
        })
        own.notificationSettings.push(setlTxnSms)
      }
      if (settlementTranEmail) {
        const sTxnEmail = NotificationSetting.create({
          channel: ['n', 'y', 'n'],
          type: NotificationType.settlementReportGen,
        })
        own.notificationSettings.push(sTxnEmail)
      }
    },
    updateNotificationEmail(newEmail: string): void {
      const own = self
      own.notificationEmailId = newEmail
    },
    updateNotificationMobileNumber(newMobile: string): void {
      const own = self
      own.notificationMobileNumber = newMobile
    },
    setTrackingId(trackingId: string): void {
      const own = self
      own.metaData.trackingId = trackingId
    },
    fetchUserSettings: flow(function* fetchUserSettings(otpAuthCode?: any) {
      let response = {}
      const reqBody: any = {
        externalReferenceId: RootStore.user.selectedMid,
        orgName: `Merchant${RootStore.user.selectedMid}`,
        appName: 'Merchant-' + RootStore.user.selectedMid,
        mobileNumber: RootStore.settings.metaData.mobileNumber,
        /* validations: {
          type: 1,
          otpAuthCode: otpAuthCode,
        }, */
      }
      if (otpAuthCode) {
        reqBody.validations = {
          type: 1,
          otpAuthCode: otpAuthCode,
        }
        reqBody.authenticationMode = 18
        reqBody.authenticationPurpose = 1
      }
      try {
        response = yield getUserSettings(RootStore.user.session.getTokens, reqBody)
        self.apiCredentials = response as ApiCredentialsType
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? customError.errorOccured)
      }
      return response
    }),
    sendOTP: flow(function* sendOTP(mobileNum: string) {
      let response = {}
      try {
        response = yield generateOTP(
          mobileNum,
          RootStore.user.session.getTokens,
          getGenerateOtpReqForApiCred(mobileNum),
        )
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        // throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    getOTPAuthCode: flow(function* getOTPAuthCode(otp: string) {
      let response: any = {}
      try {
        response = yield getOTPAuthCodeApi(
          RootStore.settings.metaData.mobileNumber,
          otp,
          RootStore.user.session.getTokens,
        )
        if (response?.authorizationCode) {
          yield self.fetchUserSettings(response?.authorizationCode)
        } else {
          RootStore?.response?.setMessage(customError.errorOccured)
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        return { error: e?.data?.error?.message ?? 'Error while processing request' }
      }
      return response
    }),
  }))
  .views((self) => ({
    getApiCredList() {
      const apiCredList = [
        {
          title: 'MID',
          subTittle: 'Unique identifier for your account',
          value: self?.apiCredentials?.mid,
          iconId: ic_copy,
          iconAction: 'COPY',
        },
        {
          title: 'Client Id',
          subTittle: 'Pass this value in client id field of Authenticate API',
          value: self?.apiCredentials?.clientId,
          iconId: ic_copy,
          iconAction: 'COPY',
        },
        {
          title: 'Key Value',
          subTittle: 'Pass this value in client secret field of Authenticate API',
          value: self?.apiCredentials?.keyValue,
          iconId: ic_lock,
          iconAction: 'LOCK',
        },
        {
          title: 'Creation Date',
          subTittle: '',
          value: self?.apiCredentials?.creationDate,
        },
      ]
      return apiCredList
    },
  }))

const initMetaData = MetaData.create({
  trackingId: '',
  selectedMercName: '',
  selectedInstitutionId: '0',
  selectedMerchantStatus: '',
  signupLineOfBusiness: '',
  signupBusinessType: '',
  signupAcceptedPayments: [],
  signupAnnualRevenue: '',
  signupCompanyName: '',
  signupCompanyAddress: '',
  signupRoleInCompany: '',
  signupEmail: '',
  signupMobile: '',
  signupPincode: '',
  recentSearches: [],
  bankList: [],
  mobileNumber: '',
  selectedBankId: '',
  videos: [],
  isAppLockEnabled: false,
  isSetAppLockLater: false,
  showSearchResult: false,
  searchString: '',
  searchFilterItem: 'JioPay Id',
  selectedDate: {
    fromDate: getSelectedDate(1),
    toDate: new Date(Date.now()),
    position: {
      top: 0,
      left: 0,
      visible: false,
    },
  },
  dateValue: 'Today',
  vpa: '',
  staticQr: '',
  disableBulkRefund: false,
})
const initApiCredentials = ApiCredentials.create({
  mid: '',
  keyValue: '',
  clientId: '',
  creationDate: '',
})

const settingsData = Settings.create({
  notificationSettings: [notificationSettingData],
  notificationMobileNumber: 'NA',
  notificationEmailId: 'NA',
  config: configData,
  metaData: initMetaData,
  apiCredentials: initApiCredentials,
})

export { initMetaData, Settings, settingsData }
export type ApiCredentialsType = Instance<typeof ApiCredentials>
export type SettingsV2 = Instance<typeof Settings>
export type MetaDataV2 = Instance<typeof MetaData>
export type BankListType = Instance<typeof BankList>
export type VideosType = Instance<typeof Videos>
